import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { requestWelcomeEmail } from "../utils/api";
import { createWelcomeRequester } from "./welcomeRequest";

const AuthContext = createContext(null);

console.log("AuthProvider mounted");

// W2-R1: ask the worker for the welcome email on a verified session. The rules
// (once accepted, not again this browser session; retried after a failure; reset
// on sign-out) live in ./welcomeRequest so they can be tested without React.
const welcome = createWelcomeRequester({
  request: requestWelcomeEmail,
  storage: () => window.sessionStorage,
  // Deferred out of the onAuthStateChange callback, which supabase-js runs inside
  // its auth lock; requestWelcomeEmail reads the session.
  defer: (fn) => setTimeout(fn, 0),
});

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setLoading(false);
      if (data.session) welcome.maybeRequest(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((evt, next) => {
      setSession(next ?? null);
      // Any event that carries a session can (re)ask: after a request the worker
      // did not accept, the next one -- including an hourly TOKEN_REFRESHED -- tries
      // again. Once accepted, the guard stops repeats for this browser session.
      if (evt === "SIGNED_OUT") welcome.forget();
      else if (next) welcome.maybeRequest(next);
    });

    return () => sub?.subscription?.unsubscribe?.();
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      signOut: () => supabase.auth.signOut(),
      signInWithOtp: (email) => supabase.auth.signInWithOtp({ email }),
    }),
    [session, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
